import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChartLineUp, FileText, ShieldCheck } from "@phosphor-icons/react";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/i18n";

const ICONS = { ChartLineUp, FileText, ShieldCheck };
const IMG = "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?auto=format&fit=crop&w=1400&q=80";

export default function Management() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="gestione" ref={ref} data-testid="management-section" className="bg-navy py-28 text-ivory md:py-40">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
        <Reveal>
          <Overline>{t.management.overline}</Overline>
          <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-5xl lg:text-6xl">
            {t.management.title}
          </h2>
          <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-ivory/70 md:text-lg">
            {t.management.text}
          </p>

          <ul className="mt-12 space-y-6">
            {(t.management.bullets || []).map((b, i) => {
              const Icon = ICONS[b.icon];
              return (
                <li key={i} data-testid={`management-bullet-${i + 1}`} className="flex items-center gap-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-champagne/40">
                    <Icon size={20} weight="light" className="text-champagne" />
                  </span>
                  <span className="text-sm font-light text-ivory/80 md:text-base">{b.text}</span>
                </li>
              );
            })}
          </ul>
        </Reveal>

        <Reveal delay={0.15} className="relative">
          <div className="absolute -right-4 -top-4 hidden h-full w-full border border-champagne/40 lg:block" />
          <div className="relative overflow-hidden">
            <motion.img
              style={{ y: imgY }}
              src={IMG}
              alt="Casa vacanza gestita da Élite Service nel Golfo del Tigullio"
              loading="lazy"
              className="aspect-[4/5] w-full scale-110 object-cover lg:aspect-[5/6]"
            />
            <div className="absolute inset-0 bg-navy/15" />
          </div>
          <p className="mt-4 font-serif text-sm italic text-ivory/50">{t.management.imageCaption}</p>
        </Reveal>
      </div>
    </section>
  );
}
